//#region Imports
import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  createContext,
  useContext
} from "react"

import { useForm } from "react-hook-form"

import _ from "lodash"

//#region Components
import { getPageConfig } from "../../pages/Config"
import Button, { SVGIcon } from "../../components/generic/buttons/Button"
import TransactionItem from "../../components/transaction/TransactionItem"
import { ResponsePane } from "../../components/ResponsePane"

//#endregion

//#region API
import { Flights } from "../../api/Flight"
import { Transaction, Transactions } from "../../api/Transaction"
//#endregion

//#region Global state
import {
  IFlightSelection,
  updateAirportSeasonsSelection,
  updateUserSettingsSelection
} from "../../containers/selectionSlice"

import {
  beginTransaction,
  endTransaction
} from "../../containers/transactionSlice"
//#endregion

import { getAirportSeasonConfigurations } from "../../api/BackendCalls"
import {
  ActionType,
  EditMode,
  FlightMode,
  GCRMode,
  handleHasFlightsWithParkingLoc,
  handleHasFlightsWithServiceTypeAcReg,
  handleHasFlightsWithTerminal,
  PageGroup
} from "../../common/transaction"
import SearchCriteria from "../../components/SearchCriteria"
import { handleGetTransactionPageGroup, PageId } from "../../common/pageId"
import { FragmentValues } from "../../components/BreakoutPeriod"
import { ResizeOverlay } from "../../components/ResizeOverlay"
import SwitchBtn from "../../components/generic/form/SwitchBtn"
import SeasonSelector from "../../components/SeasonSelector"
import TransactionFlightHeaders from "../../components/transaction/TransactionFlightHeaders"
import { searchHistory, timeOptions } from "../pageUtils"
import useInputAlert from "../../hooks/useInputAlert"
import { FlightTable } from "../../components/table/FlightTableGrid"
import { AppContext } from "../../app/App"
import {
  handleDeleteAllTransactions,
  handleDeleteTransactions,
  handleGetFlightReport,
  handleMakeEditTransactions,
  handleRefreshFlightsAndTransactions
} from "../Support"
import {
  BtnText,
  FieldName,
  FormText,
  LoadingText,
  userSettingsKey
} from "../../common/text"
import FlightSelector, {
  IFlightSelectorValues
} from "../../components/FlightSelector"
import * as Utils from "../../components/Utils"
import useErrorHandling from "../../hooks/useErrorHandling"
import * as Support from "../Support"
import { useFlightSelectorStateValues } from "../../hooks/useFlighSelectorInitValues"
import store from "../../app/Store"
import { NotificationKind, notify } from "../../containers/notificationSlice"
import SelectorPanel from "../../components/FlightSelectorPanel"
import { PageContent } from "../../common/pageText"
import { isFieldEditable, showField } from "../../components/transaction/Config"
import { FlightReports } from "../../api/FlightReport"
import { ChangeInputType } from "../../api/EventTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { CodeType } from "../../components/AirportSelector"

//#endregion

interface Props {
  pageId: PageId
  pageType?: string
}

interface ActionMap {
  [transId: number]: ActionType
}
interface IOpts {
  label: string
  value: string
  id: string
}
export interface IEditContext {
  activeSearch: boolean
  options: IOpts[]
  handleRadioChange: (e: ChangeInputType) => void
  value: boolean
  caption: string
}
export interface ContextType {
  handleTimeChange: () => void
  handleCloseModal: () => void
  handleDispatchSeasons?: () => void
  local: boolean
  showModal: boolean
  selectedTime: string
  timeOptions: IOpts[]
  activeSearch: boolean
  handleDispatchTime: () => void
  showContext?: boolean
}

export const EditContext = createContext<ContextType>({
  handleTimeChange: () => {},
  handleCloseModal: () => {},
  local: true,
  showModal: false,
  selectedTime: "",
  timeOptions,
  activeSearch: false,
  handleDispatchTime: () => {}
})
export const EditFlights: React.FunctionComponent<Props> = ({
  pageId,
  pageType
}) => {
  //#region local state
  const [airportSeasons, setAirportSeasons] = useState([])
  const [flights, setFlights] = useState<Flights>([])
  const [handleAsBlock, setHandleAsBlock] = useState(false)
  const { showOptionalFields, pinSelectorPanel } = useAppSelector(
    (state) => state["selectionReducer"].userSettings
  )
  const { handleSubmit } = useForm()

  const [selectedFlightIds, setSelectedFlightIds] = useState<number[]>([])
  const [transactions, setTransactions] = useState<Transactions>([])
  const [initialTransactions, setInitialTransactions] = useState<Transactions>(
    []
  )

  const [actions, setActions] = useState<ActionMap>({})
  const [transactionInFocus, setTransactionInFocus] = useState<number>(null)
  const [breakoutValues, setBreakoutValues] = useState<FragmentValues>({
    separationOption: "",
    doop: "",
    splitOption: "",
    startDate: null,
    endDate: null
  })
  const contentContainer = useRef<HTMLDivElement>(null)
  const transactionPageGroup = handleGetTransactionPageGroup(pageId)
  const isGabaPage = transactionPageGroup === PageGroup.GABA
  const appContext = useContext(AppContext)
  const isHandling =
    pageId === PageId.EditHandling || pageId === PageId.ViewHandling
  //#endregion

  //#region global state
  const { season, airports, countries, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const historySelection = useAppSelector(
    (state) => state["selectionReducer"].pagesHistory
  )
  //#endregion
  const [activeSearch, setActiveSearch] = useState(false)
  const [collapsedSidePanel, setCollapsedSidePanel] = useState(false)
  const [invalidFieldsArr, setInvalidFieldsArr] = useState<string[]>([])

  useEffect(() => {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: LoadingText.loadingAirportSeasons
    })
    getAirportSeasonConfigurations(
      pageId,
      (airportSeasons) => {
        setAirportSeasons(airportSeasons)
        appContext.handleSetIsLoading({ isLoading: false })
      },
      () =>
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
    )
    pageType !== EditMode.VIEW && handleDeleteAllTransactions(pageId)

    return () => {
      //cleanup on unmount
      dispatch(endTransaction({}))
    }
    // eslint-disable-next-line
  }, [pageId])

  const isEditPage = pageType === EditMode.EDIT
  useEffect(() => {
    setFlights([])
    setActiveSearch(false)
    setTransactions([])
    setInvalidFieldsArr([])
    pageType !== EditMode.VIEW && handleDeleteAllTransactions(pageId)
    // eslint-disable-next-line
  }, [pageId, season])

  const pageHistory = historySelection[PageId.All]
  const selectedHistory = searchHistory(pageHistory, "addToQuery")

  const { lastSelection, searchCriterias } = selectedHistory
  const historyRowAirports: any = searchCriterias.reduce(
    (r, obj) => r.concat(obj.airports),
    []
  )
  //#region API calls
  async function deleteTransactionsAndRefresh(
    parameters: any,
    onSuccess?: () => void
  ) {
    setInvalidFieldsArr([])
    handleDeleteTransactions(
      pageId,
      parameters,
      lastSelection?.delink ?? false,
      useLocalTime,
      lastSelection?.normalizePeriod ?? false,
      (flights, newTransactions) => {
        handleRefreshFlightsAndTransactionsSuccess(flights, newTransactions)
        onSuccess && onSuccess()
      },
      () => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
      }
    )
  }

  async function deleteAllTransactionsAndRefresh() {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: "Deselecting Flights..."
    })
    deleteTransactionsAndRefresh({ transIds: [-1] }, () => {
      setActions({})
    })
  }

  const pageConfig = getPageConfig[pageId]
  const pageTitle = pageConfig.pageTitle
  const codeType = pageConfig.codeType
  const flightSelectorConfig = pageConfig.flightSelectorConfig
  const airportseasonSelectorConfig = pageConfig.airportseasonSelectorConfig
  const fragmentConfig = pageConfig?.fragmentConfig
  const actionConfig = pageConfig?.actionConfig

  const handleActionSubmit = (command: ActionType) => {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: "Selecting Flights..."
    })

    const parameters = {
      season: season,
      flightIds: selectedFlightIds,
      action: command,
      dateFrom: breakoutValues.startDate || "",
      dateTo: breakoutValues.endDate || "",
      doop: breakoutValues.doop,
      separationOption: breakoutValues.separationOption || "",
      splitOption: breakoutValues.splitOption || ""
    }

    handleMakeEditTransactions(
      pageId,
      parameters,
      lastSelection?.delink ?? false,
      useLocalTime,
      lastSelection?.normalizePeriod ?? false,
      (flights, newTransactions) =>
        handleRefreshFlightsAndTransactionsSuccess(
          flights,
          newTransactions,
          command
        ),
      () => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
      }
    )
  }

  const refreshFlightsAndTransactions = () => {
    handleRefreshFlightsAndTransactions(
      pageId,
      lastSelection?.delink ?? false,
      useLocalTime,
      lastSelection?.normalizePeriod ?? false,
      (flights, newTransactions) =>
        handleRefreshFlightsAndTransactionsSuccess(flights, newTransactions),
      () => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
      }
    )
  }

  const handleRefreshFlightsAndTransactionsSuccess = (
    flights: Flights,
    newTransactions: Transactions,
    action?: ActionType
  ) => {
    setFlights(flights)
    handleSetTransactions(newTransactions)
    handleSetInitialTransactions(newTransactions)

    setSelectedFlightIds([])
    setBreakoutValues({
      separationOption: "",
      doop: "",
      splitOption: "",
      startDate: null,
      endDate: null
    })
    if (action) {
      handleSetTransactionAction(newTransactions, action)
    }
    appContext.handleSetIsLoading({ isLoading: false })
  }
  //#endregion

  const handleSetInitialTransactions = (newTrans: Transaction[]) => {
    const updInitTransactions = newTrans.map((nt) => {
      return initialTransactions.find((t) => t.transId === nt.transId) ?? nt
    })

    const hasFlightsWithParkingLoc = handleHasFlightsWithParkingLoc(
      airportSeasons,
      updInitTransactions
    )

    if (
      hasFlightsWithParkingLoc &&
      updInitTransactions.find((transaction) =>
        transaction.flights.find((flight) => flight.parkingLocation === "")
      )
    ) {
      setInvalidFieldsArr([...invalidFieldsArr, "parkingLocation"])
    }

    setInitialTransactions(updInitTransactions)
  }

  const handleSetTransactions = (newTrans: Transaction[]) => {
    const updTransactions = newTrans.map((nt) => {
      return transactions.find((t) => t.transId === nt.transId) ?? nt
    })
    setTransactions(updTransactions)
    //setTransactionInFocus(updTransactions.length - 1)
    setTransactionInFocus(0)
  }

  const handleSetTransactionAction = (
    transactions: Transaction[],
    action: ActionType
  ) => {
    const updActions = _.cloneDeep(actions)
    const newTransactions = transactions.filter((t) => !actions[t.transId])
    newTransactions.forEach((nt) => (updActions[nt.transId] = action))
    setActions(updActions)
  }

  const handleDeleteTransactionAction = (transId: number) => {
    const updActions = _.cloneDeep(actions)
    delete updActions[transId]
    setActions(updActions)
  }

  //#region Event handlers

  const getFlightsLastSelected = () => {
    Support.handleRefreshFlights(
      pageId,
      {
        useLocalTime: useLocalTime,
        delink: lastSelection?.delink ?? false,
        normalizePeriod: lastSelection?.normalizePeriod ?? false
      },
      (flights) => {
        appContext.handleSetIsLoading({ isLoading: false })
        setFlights(flights)
      },
      () => {
        appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
      }
    )
  }

  const handleDeleteTransaction = (transIdx: number) => {
    appContext.handleSetIsLoading({
      isLoading: true,
      loadingTxt: "Deselecting Flight..."
    })
    const transId = transactions[transIdx].transId

    deleteTransactionsAndRefresh({ transIds: [transId] })
    handleDeleteTransactionAction(transId)
    setTouched(false)
  }

  const handleFillDown = (
    transIdx: number,
    flightIdx: number,
    field: FieldName
  ) => {
    const updTransactions = _.cloneDeep(transactions)
    const fillDownVal = transactions[transIdx].flights[flightIdx][field]

    for (let i = transIdx + 1; i < transactions.length; i++) {
      updTransactions[i].flights.forEach((flight, idx) => {
        if (
          showField(
            field,
            flight.flightMode,
            EditMode.EDIT,
            transactions[i].gcrMode
          ) &&
          isFieldEditable(field, flight.requestCode, EditMode.EDIT)
        ) {
          flight[field] = fillDownVal
        }
      })
    }

    setTransactions(updTransactions)
  }

  const [touched, setTouched] = useState(false)

  const handleUpdTransaction = (
    newTransaction: Transaction,
    transIdx: number,
    error: boolean,
    field: string
  ) => {
    setTouched(false)

    if (error && !invalidFieldsArr.includes(field)) {
      setInvalidFieldsArr([...invalidFieldsArr, field])
    }
    if (!error) {
      setInvalidFieldsArr(invalidFieldsArr.filter((a) => a !== field))
    }
    const updTransactions = _.cloneDeep(transactions)
    updTransactions[transIdx] = newTransaction
    setTransactions(updTransactions)
  }
  //#endregion

  const dispatch = useAppDispatch()

  const startTransaction = () => {
    console.log("Invalid Fields =>", invalidFieldsArr)
    if (error) {
      dispatch(
        updateUserSettingsSelection({
          field: userSettingsKey.showOptionalFields,
          value: true
        })
      )
    }
    pageType !== EditMode.VIEW && setTouched(true)

    if (invalidFieldsArr.length === 0 && pageType !== EditMode.VIEW) {
      dispatch(beginTransaction({}))
    }
  }

  const customShowLabelConfig = {
    terminal: {
      showLabel: true,
      showLabelSection: handleHasFlightsWithTerminal(
        airportSeasons,
        transactions
      )
    },
    parkingLocation: {
      showLabel: true,
      showLabelSection: handleHasFlightsWithParkingLoc(
        airportSeasons,
        transactions
      )
    },
    serviceTypeAcReg: {
      showLabel: true,
      showLabelSection: handleHasFlightsWithServiceTypeAcReg(
        airportSeasons,
        transactions
      )
    }
  }

  const transItemsIds = transactions.map((t) => "transItem" + t.transId)
  const transactionItems = transactions.map((t, i) => (
    <TransactionItem
      key={i}
      transaction={t}
      editMode={EditMode.EDIT}
      isEditSeries={isEditPage}
      actionType={actions[t.transId]}
      airportSeasons={airportSeasons}
      codeType={codeType}
      pageGroup={transactionPageGroup}
      onDelete={() => handleDeleteTransaction(i)}
      onChange={(newTransaction: Transaction, error, field) => {
        if (error) {
          setError(true)
        }

        return handleUpdTransaction(newTransaction, i, error, field)
      }}
      onChangeInFocus={(isInFocus) =>
        setTransactionInFocus(isInFocus ? i : null)
      }
      inFocus={transactionInFocus === i}
      transactionItemIDs={transItemsIds}
      initialTransaction={initialTransactions.find(
        (it) => it.transId === t.transId
      )}
      placement={"top"}
      touched={touched}
      onFillDown={(flightIdx, field) => handleFillDown(i, flightIdx, field)}
      customShowLabelConfig={customShowLabelConfig}
    />
  ))

  const dispatchFn = () => {
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.useLocalTime,
        value: local
      })
    )
  }

  const {
    handleTimeChange,
    handleCloseModal,
    handleResetFlights,
    local,
    showModal,
    selectedTime
  } = useInputAlert(activeSearch, dispatchFn, useLocalTime)

  const handleDispatchTime = () => {
    handleResetFlights()
    setActiveSearch(false)
    setFlights([])
    setTransactions([])
    setInvalidFieldsArr([])
    handleDeleteAllTransactions(pageId)
  }
  const editValues = {
    handleTimeChange,
    handleCloseModal,
    local,
    showModal,
    selectedTime,
    timeOptions,
    activeSearch,
    handleDispatchTime,
    showContext: true
  }

  const notOptionalRequestCodes = ["D", "F", "A", "P", "Z"]

  //flightSelectorPanel stuff
  const flightSelectorValues = useFlightSelectorStateValues()
  const [flightSelectorState, setFlightSelectorState] =
    useState<IFlightSelectorValues>(flightSelectorValues)

  const flightSelection: IFlightSelection = useAppSelector(
    (state) => state["selectionReducer"].flightSelector
  )

  useEffect(() => {
    if (appContext.userChanged) {
      if (!_.isEqual(flightSelectorValues, flightSelectorState)) {
        setFlightSelectorState(flightSelectorValues)
      }
      appContext.handleChangeUserChanged(false)
    }
    //eslint-disable-next-line
  }, [flightSelection])

  const selectedAirports = airports.length
    ? airports
    : countries.length
      ? airportSeasons
          .filter(
            (as) =>
              as.season === season &&
              countries.indexOf(as.country) >= 0 &&
              (useLocalTime ? as.supportsLt : as.supportsUtc)
          )
          .map((a) => a.airport)
      : []
  const selectedAirportsObj = {
    airportSeasons,
    airports,
    countries,
    season,
    useLocalTime
  }
  const seasonExists =
    airportSeasons && (airportSeasons === null || airportSeasons.length === 0)
  const isMulti = airportseasonSelectorConfig?.dropdownMultiSelect

  //START Functions for handleErrors

  const config = Utils.getFlightSelectorConfiguration(
    airportSeasons,
    season,
    selectedAirports,
    codeType
  )

  const getValues = (): IFlightSelectorValues => {
    return flightSelectorState
  }

  // const operatorViewRestricted = pageId === PageId.ViewFlightsBaseline
  // const useOwnOperators = getValues().useOwnOperators || operatorViewRestricted

  // const handlerValues = isHandling && getValues().handler
  const operatorOptions = config && config.operators
  const operatorRestricted = config && config.operatorRestricted

  const [fieldName, setFieldName] = useState("")
  const [error, setError] = useState(false)
  const editMode = pageType ? pageType : EditMode.EDIT
  const {
    errors,
    showAlert,
    invalidFieldsArrs,
    errorMsg,
    lockedOrDisabledArr,
    showSupportDownloadNotification,
    supportErrorMsg,
    fieldNameErrors
  } = useErrorHandling(
    pageId,
    selectedAirportsObj,
    getValues(),
    fieldName,
    error,
    isMulti,
    editMode,
    operatorRestricted,
    operatorOptions,
    historyRowAirports,
    editMode
  )

  //END Functions for handleErrors
  const handleChange = (field: string, val: any, error: boolean) => {
    const updFlightSelectorState = _.cloneDeep(flightSelectorState)
    updFlightSelectorState.useOwnOperators = false
    setFlightSelectorState(updFlightSelectorState)

    setFieldName(field)
    setError(error)
    setTouched(false)
  }

  const handleFlightSelectorFieldChange = (
    field: string,
    val: string,
    error: boolean
  ) => {
    setFieldName(field)
    setError(error)
    setTouched(false)
    if (error && !invalidFieldsArr.includes(field)) {
      setInvalidFieldsArr([...invalidFieldsArr, field])
    }
    if (!error) {
      setInvalidFieldsArr(invalidFieldsArr.filter((a) => a !== field))
    }

    if (error && !invalidFieldsArr.includes(field)) {
      setInvalidFieldsArr([...invalidFieldsArr, field])
    }
    if (!error) {
      setInvalidFieldsArr(invalidFieldsArr.filter((a) => a !== field))
    }

    const updFlightSelectorState = _.cloneDeep(flightSelectorState)
    updFlightSelectorState[field] = val
    setFlightSelectorState(updFlightSelectorState)
  }

  useEffect(() => {
    if (!config?.supportsTerminalRequest) {
      setInvalidFieldsArr(invalidFieldsArr.filter((a) => a !== "terminalDep"))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.supportsTerminalRequest])

  const handleSubmitNew = () => {
    const invalidArr = Object.values(errors).filter((a) => a === true)

    const sendSubmit =
      invalidFieldsArrs.length === 0 &&
      invalidArr.length === 0 &&
      invalidFieldsArr.length === 0
    console.log("Invalid Fields =>", invalidFieldsArr, errors)
    setTouched(true)

    sendSubmit && handleSubmitSearch(false)
  }

  const handleSubmitAdd = () => {
    if (activeSearch) {
      handleSubmitSearch(true)
    }
  }

  const handleSubmitSearch = (addToQuery: boolean) => {
    const invalidArr = Object.values(errors).filter((a) => a === true)
    const sendSubmit = invalidFieldsArrs.length === 0 && invalidArr.length === 0
    console.log("Invalid Fields =>", invalidFieldsArrs)
    setTouched(true)

    const commitAirports = selectedAirports.filter(
      (item) => !lockedOrDisabledArr.includes(item)
    )

    if (sendSubmit && !showAlert) {
      appContext.handleSetIsLoading({
        isLoading: true,
        loadingTxt: "Searching for Flights..."
      })
      Support.handleSearchFlights(
        pageId,
        {
          ...flightSelectorState,
          addToQuery: addToQuery,
          season: season,
          airports: commitAirports,
          lastUpdate: flightSelectorState.lastUpdate
            ? Utils.formatDate(new Date(flightSelectorState.lastUpdate))
            : null
          // cancelledSinceDate
        },
        useLocalTime,
        (flights) => {
          appContext.handleSetIsLoading({ isLoading: false })
          setFlights(flights)
          setActiveSearch(true)

          if (!pinSelectorPanel) {
            setCollapsedSidePanel(true)
          }
        },
        () => {
          appContext.handleSetIsLoading({ isLoading: false, fromFailure: true })
        }
      )
    }
  }

  const handleDownload = (reportFormat: string) => {
    appContext.handleSetIsDownloading(true)
    const pageHistory = historySelection[PageId.All]
    const lastSelection = pageHistory.history[pageHistory.history.length - 1]
    if (showSupportDownloadNotification) {
      store.dispatch(
        notify({
          kind: NotificationKind.INFO,
          title: PageContent.information,
          message: supportErrorMsg
        })
      )
    }
    handleGetFlightReport(
      pageId,
      {
        reportFormat: reportFormat,
        useLocalTime: useLocalTime,
        delink: lastSelection?.delink ?? false,
        normalizePeriod: lastSelection?.normalizePeriod ?? false
      },
      (newReports: FlightReports) => {
        var reports: FlightReports = _.clone(appContext.flightReports)
        newReports.forEach((report) => reports.push(report))
        appContext.handleChangeFlightReports(reports)
        appContext.handleSetIsDownloading(false)
      },
      () => {
        appContext.handleSetIsDownloading(false)
      }
    )
  }

  const responseTransactions = _.cloneDeep(transactions).map((t) => {
    t.flights = t.flights.map((flight) => Utils.getFlightForSubmit(flight))
    return t
  })

  return (
    <EditContext.Provider value={editValues}>
      <ResponsePane
        transactions={responseTransactions}
        pageId={pageId}
        season={season}
        useLocalTime={useLocalTime}
        title="Edit Flights"
        airportSeasons={airportSeasons}
        codeType={codeType}
        handleAsBlock={handleAsBlock}
        siText=""
        onClose={() => {
          handleDeleteAllTransactions(pageId)
          setTransactions([])
          getFlightsLastSelected()
        }}
        onCancel={refreshFlightsAndTransactions}
        fromEditMode={EditMode.EDIT}
      />
      <div className="ocs-page-container ocs-editflights d-flex">
        <SelectorPanel
          title={pageTitle}
          isCollapsed={collapsedSidePanel}
          handleToggleCollapsed={setCollapsedSidePanel}
        >
          <form className="ocs-flight-selector-container ocs-form">
            <FlightSelector
              title={pageTitle}
              airportSeasons={airportSeasons}
              values={getValues()}
              onFieldChange={handleFlightSelectorFieldChange}
              flightSelectorConfig={flightSelectorConfig}
              airportseasonSelectorConfig={airportseasonSelectorConfig}
              handleChange={handleChange}
              codeType={codeType}
              pageId={pageId}
              touched={touched}
              errors={errors}
              fieldNameErrors={fieldNameErrors}
              hasError={
                errors.disabledOrlockedError || errors.emptyAirportField
              }
              useOwnOperatorError={errors.useOwnOperatorErrors}
              invalidDate={errors.singleDateError}
              errorMsg={errorMsg}
              onFlightSelectorChange={setFlightSelectorState}
              pageGroup={transactionPageGroup}
            />
            <div className="submit-container ocs-mt-12 d-flex justify-content-between">
              <div className="w-100p" />

              <div className="btn-margin w-100p">
                <Button
                  onClick={handleSubmit(handleSubmitAdd)}
                  type="button"
                  panelBtn
                  disabled={!activeSearch}
                  icon={SVGIcon.AddToSearch}
                  className="w-100p mq-btn-height"
                >
                  {FormText.addToSearch}
                </Button>
              </div>
              <div className="w-100p">
                <Button
                  variant="primary"
                  onClick={handleSubmit(handleSubmitNew)}
                  icon="search"
                  type="submit"
                  disabled={
                    (seasonExists && config?.operatorRestricted) || seasonExists
                  }
                  panelBtn
                  className="w-100p mq-btn-height"
                >
                  {BtnText.search}
                </Button>
              </div>
            </div>
          </form>
        </SelectorPanel>

        {activeSearch && (
          <div
            className="ocs-sidepanel-right-content overflow ocs-pt-20px"
            ref={contentContainer}
          >
            <div
              className={`page-content-wrapper d-flex flex-column h-100p ${
                isHandling ? "handling-wrapper" : ""
              }`}
            >
              <SearchCriteria
                editMode={EditMode.EDIT}
                excludeConfig={flightSelectorConfig}
                airportSeasons={airportSeasons}
                useICAOCode={codeType === CodeType.ICAO}
              />
              <FlightTable
                selection={pageType !== EditMode.VIEW}
                flights={flights}
                airportSeasons={airportSeasons}
                pageId={pageId}
                breakoutValues={breakoutValues}
                fragmentConfig={fragmentConfig}
                actionConfig={actionConfig}
                onActionClick={handleActionSubmit}
                historyRowAirports={historyRowAirports}
                isViewPageType={pageType === EditMode.VIEW}
                onChangeBreakout={(values) => {
                  setBreakoutValues(values)
                }}
                onSelect={(flightIds: number[]) => {
                  setSelectedFlightIds(flightIds)
                }}
                onRefresh={() => {
                  appContext.handleSetIsLoading({
                    isLoading: true,
                    loadingTxt: "Refreshing Flights..."
                  })
                  getFlightsLastSelected()
                }}
                onDownload={handleDownload}
                disableActionBtns={selectedFlightIds.length <= 0}
              />
              {pageType !== EditMode.VIEW && (
                <ResizeOverlay
                  maxHeight={
                    contentContainer?.current
                      ? contentContainer.current.offsetHeight - 20
                      : 800
                  }
                  amountInfo={transactions.length}
                >
                  {transactions.length > 0 && (
                    <Fragment>
                      <section className="ocs-transaction-item-actions-container">
                        <div className="d-flex align-items-center ocs-transaction-item-actions">
                          <div className="ocs-mr-16">
                            <SeasonSelector
                              airportSeasons={airportSeasons}
                              supportsICAO={false}
                              seasonReadonly
                              timeReadonly
                            />
                          </div>
                          <SwitchBtn
                            identifier={"showOptionalFields"}
                            name={"showOptionalFields"}
                            label={"Show Required Fields Only"}
                            checked={!showOptionalFields}
                            callback={(value, _name) => {
                              dispatch(
                                updateUserSettingsSelection({
                                  field: userSettingsKey.showOptionalFields,
                                  value: !showOptionalFields
                                })
                              )
                            }}
                          />
                        </div>
                        <div className="d-flex ocs-transaction-item-actions">
                          {!isGabaPage && !isHandling && (
                            <div className="ocs-mr-12">
                              <SwitchBtn
                                identifier={"handleAsBlock"}
                                name={"handleAsBlock"}
                                label={"Send as Block"}
                                checked={handleAsBlock}
                                callback={(value, _name) => {
                                  setHandleAsBlock(value)
                                }}
                              />
                            </div>
                          )}
                          <div className="d-flex">
                            <Button
                              className="btn-margin mq-btn-height"
                              disabled={!(transactions.length > 0)}
                              onClick={() => deleteAllTransactionsAndRefresh()}
                              type="button"
                            >
                              {FormText.deselectAll}
                            </Button>
                            <Button
                              disabled={!(transactions.length > 0)}
                              className="mq-btn-height"
                              onClick={handleSubmit(startTransaction)}
                              variant={"primary"}
                              type="submit"
                              idName="sendAllBtn"
                            >
                              {FormText.sendAll}
                            </Button>
                          </div>
                        </div>
                      </section>
                      <section
                        className={
                          "ocs-mt-20 ocs-change-screen-transactions overflow " +
                          (transactionInFocus !== null
                            ? notOptionalRequestCodes.find(
                                (c) =>
                                  c ===
                                  transactions[transactionInFocus]?.flights[0]
                                    .requestCode
                              )
                              ? "ocs-mb-24"
                              : transactions[transactionInFocus]?.flights
                                    .length === 2 &&
                                  transactions[transactionInFocus]?.flights[0]
                                    .flightMode !== FlightMode.Turnaround
                                ? "ocs-mb-12"
                                : ""
                            : "ocs-mb-44")
                        }
                      >
                        <form className="ocs-form transactions-container">
                          <TransactionFlightHeaders
                            editMode={EditMode.EDIT}
                            flightMode={FlightMode.Turnaround}
                            codeType={codeType}
                            pageGroup={transactionPageGroup}
                            grcMode={GCRMode.FLT}
                            sticky
                            customShowLabelConfig={customShowLabelConfig}
                          />
                          {transactionItems}
                        </form>
                      </section>
                    </Fragment>
                  )}
                </ResizeOverlay>
              )}
            </div>
          </div>
        )}
      </div>
    </EditContext.Provider>
  )
}
